import { Component, ElementRef, EventEmitter, Injector, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConnectedPosition, OverlayModule, createRepositionScrollStrategy } from '@angular/cdk/overlay';
import { Store } from '@ngrx/store';
import { AppState, selectUserSearchResults } from '../../ngrx/app.state';
import { UserDetail } from '../../models/UserDetail.model';
import * as GarageActions from '../../ngrx/actions/garage.actions';

@Component({
  selector: 'app-user-search-typeahead',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule],
  templateUrl: './user-search-typeahead.component.html',
  styleUrls: ['./user-search-typeahead.component.scss']
})
export class UserSearchTypeaheadComponent implements OnInit {
  @Input() placeholder: string = 'Search users...';
  @Input() selectedUser: UserDetail | null = null;
  @Output() userSelected = new EventEmitter<UserDetail>();

  @ViewChild('originEl') originEl?: ElementRef<HTMLElement>;

  searchTerm: string = '';
  allUsers: UserDetail[] = [];
  filteredUsers: UserDetail[] = [];
  isDropdownOpen: boolean = false;
  isLoading: boolean = false;

  // Reposition rather than close, so the list stays glued to the input when a
  // host modal body or the page scrolls underneath it. Hosts must mark their
  // scroll container with cdkScrollable — ScrollDispatcher's global listener is
  // on document, and element scroll events do not bubble.
  scrollStrategy = createRepositionScrollStrategy(inject(Injector));
  // Matched to the input each time the list opens. '' rather than 0 means
  // "unmeasured": CdkConnectedOverlay treats a literal 0 as a real width.
  triggerWidth: number | string = '';

  // Below the input, flipping above it when the viewport has no room beneath.
  readonly dropdownPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
  ];

  constructor(
    private store: Store<AppState>,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    // Subscribe to user search results from the store
    this.store.select(selectUserSearchResults).subscribe({
      next: (users) => {
        if (users) {
          this.allUsers = users.filter(user => user.isActive);
          this.filteredUsers = [...this.allUsers];
          this.isLoading = false;
        } else {
          // Only dispatch if users don't exist yet
          this.loadUsers();
        }
      }
    });
  }

  loadUsers(): void {
    this.isLoading = true;
    this.store.dispatch(GarageActions.loadUsers());
  }

  onInputFocus(): void {
    this.openDropdown();
    this.filterUsers();
  }

  onInputChange(): void {
    this.filterUsers();
    if (!this.isDropdownOpen) {
      this.openDropdown();
    }
  }

  filterUsers(): void {
    const term = this.searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredUsers = [...this.allUsers];
    } else {
      this.filteredUsers = this.allUsers.filter(user =>
        user.email.toLowerCase().includes(term) ||
        user.firstName.toLowerCase().includes(term) ||
        user.lastName.toLowerCase().includes(term)
      );
    }
  }

  selectUser(user: UserDetail): void {
    this.selectedUser = user;
    this.searchTerm = user.email;
    this.isDropdownOpen = false;
    this.userSelected.emit(user);
  }

  clearSelection(): void {
    this.selectedUser = null;
    this.searchTerm = '';
    this.filterUsers();
  }

  onOutsideClick(event: MouseEvent): void {
    // CDK already excludes clicks on the panel; the trigger is still reported
    // here, so guard it to avoid closing and immediately reopening on focus.
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isDropdownOpen = false;
    }
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      // Hosting modals are opened with keyboard: false, but stop the key here
      // anyway so closing the list can never dismiss the modal behind it.
      event.stopPropagation();
      this.isDropdownOpen = false;
    }
  }

  private openDropdown(): void {
    const trigger = this.originEl?.nativeElement;
    // getBoundingClientRect over offsetWidth: subpixel-accurate, so the panel
    // edges line up exactly with the input's.
    this.triggerWidth = trigger ? trigger.getBoundingClientRect().width : '';
    this.isDropdownOpen = true;
  }
}
